import { Anchor, Badge, Button, Group, Modal, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { EmptyNote } from '../../components/RecordScreen';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDate, formatDateTime, formatMoney, formatQty } from '../../lib/format';
import { DeliveryDetailModal } from './DeliveryDetail';
import { orderQuery, outstandingQty } from './orders';
import { ReceiveDeliveryForm } from './ReceiveDeliveryForm';

/**
 * What an order was actually for.
 *
 * Its lines existed on screen for exactly as long as the form that created them was open. After
 * that the portal held the order — supplier, status, sent date, and the note somebody typed — and
 * no screen anywhere could say what was on it. So "did I already order limes?" had no answer in
 * the system that had the answer, and receiving the order meant typing every line again off the
 * paper.
 *
 * The delivery is recorded from HERE, on the order it is a delivery of, rather than from a tab
 * that then asks who delivered it and against which order.
 */
export function OrderDetailModal({
  orderId,
  locationId,
  onClose,
  onChanged,
}: {
  orderId: string;
  locationId?: string;
  onClose: () => void;
  /** Fired when a delivery is recorded, so the list underneath stops showing the old status. */
  onChanged?: () => void;
}) {
  const items = useEntityIndex('inventoryItem');
  const [receiving, setReceiving] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const order = useQuery(orderQuery(orderId));

  const detail = order.data;
  const currency = detail?.lines[0]?.currency ?? 'HNL';
  /** Something has come already, so what is offered next is the remainder and should say so. */
  const partly = (detail?.lines ?? []).some((l) => l.receivedQty > 0);
  const open = detail?.po.status === 'sent' || detail?.po.status === 'partiallyReceived';

  return (
    <>
      <Modal opened onClose={onClose} size="xl" title={`Order to ${detail?.supplierName ?? 'this supplier'}`}>
        <Stack gap="md">
          {order.isLoading ? (
            <EmptyNote>Loading…</EmptyNote>
          ) : order.isError || !detail ? (
            // Never an empty order: "nothing on it" over a five-second outage is a fact about the
            // business, and this one would have somebody ordering the whole lot a second time.
            <EmptyNote>
              This order could not be loaded. Nothing has been lost — try again in a moment.
            </EmptyNote>
          ) : (
            <>
              <Group gap="md">
                <Badge variant="light" color={enumColor('purchaseOrderStatus', detail.po.status) ?? 'gray'}>
                  {enumLabel('purchaseOrderStatus', detail.po.status)}
                </Badge>
                <Text size="sm" c="dimmed">
                  {detail.po.orderedAt ? `Sent ${formatDateTime(detail.po.orderedAt)}` : 'Not sent yet'}
                  {detail.po.note ? ` · ${detail.po.note}` : ''}
                </Text>
              </Group>

              {detail.lines.length === 0 ? (
                <EmptyNote>Nothing was written down against this order.</EmptyNote>
              ) : (
                <Table.ScrollContainer minWidth={560}>
                  <Table verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Item</Table.Th>
                        <Table.Th ta="right">Ordered</Table.Th>
                        <Table.Th ta="right">Arrived</Table.Th>
                        <Table.Th ta="right">Price</Table.Th>
                        <Table.Th ta="right">Comes to</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {detail.lines.map((line) => {
                        const short = outstandingQty(line);
                        return (
                          <Table.Tr key={line.id}>
                            <Table.Td>{items.label(line.inventoryItemId)}</Table.Td>
                            <Table.Td ta="right">{formatQty(line.qty, line.unit)}</Table.Td>
                            <Table.Td ta="right">
                              {line.receivedQty > 0 ? formatQty(line.receivedQty, line.unit) : '—'}
                              {/* The number a manager is chasing, said where it is noticed, rather
                                  than left as a subtraction between two columns. */}
                              {short > 0 && line.receivedQty > 0 && (
                                <Text size="xs" c="orange.8">
                                  {formatQty(short, line.unit)} short
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td ta="right">
                              {formatMoney(line.unitPriceMinor, line.currency)}
                              {line.unit ? ` a ${line.unit}` : ''}
                            </Table.Td>
                            <Table.Td ta="right">
                              {formatMoney(Math.round(line.unitPriceMinor * line.qty), line.currency)}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}

              <Group justify="flex-end">
                <Text size="sm">
                  The order came to <strong>{formatMoney(detail.totalMinor, currency)}</strong>
                </Text>
              </Group>

              {detail.receipts.length > 0 && (
                <div>
                  <Text size="sm" fw={500}>
                    Delivered against it
                  </Text>
                  <Stack gap={2} mt={4}>
                    {/* Openable, because this row is the only place the question "what did it
                        actually cost me once the ferry was paid" is asked. The order knows the
                        price the vendor quoted; the landed cost is on the delivery. Sending the
                        manager to the deliveries tab to find this same delivery by date is the
                        detour the order screen exists to remove. */}
                    {detail.receipts.map((r) => (
                      <Anchor
                        key={r.id}
                        component="button"
                        type="button"
                        size="sm"
                        ta="left"
                        onClick={() => setOpening(r.id)}
                      >
                        {formatDate(r.receivedAt)}
                        {r.invoiceNumber ? ` · invoice ${r.invoiceNumber}` : ' · no invoice number'}
                      </Anchor>
                    ))}
                  </Stack>
                </div>
              )}

              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>
                  Close
                </Button>
                {/* On the order, because the delivery IS of this order: opened from the deliveries
                    tab the same form asks who delivered it and which order it was against, both of
                    which are on the screen the manager is already looking at. */}
                {open && (
                  <Button onClick={() => setReceiving(true)}>
                    {partly ? 'Record the rest of the delivery' : 'Record the delivery'}
                  </Button>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      {receiving && detail && (
        <ReceiveDeliveryForm
          locationId={locationId}
          supplierId={detail.po.supplierId}
          purchaseOrderId={detail.po.id}
          onClose={() => setReceiving(false)}
          onSaved={() => {
            void order.refetch();
            onChanged?.();
          }}
        />
      )}

      {opening && <DeliveryDetailModal receiptId={opening} onClose={() => setOpening(null)} />}
    </>
  );
}
